/* eslint-disable react-hooks/exhaustive-deps */
import React,{useEffect,useState} from 'react';
import {useNavigate,useParams} from 'react-router-dom';
import Layout from '../components/layout/Layout';
import DataTable from '../components/common/DataTable';
import {Input,Select} from '../components/common/FormField';
import api from '../services/api';
import {fmtDateTime} from '../utils/helpers';
import {prettyMortuary} from '../utils/mortuaryLabels';

const unwrap=(r:any)=>r?.data?.data??r?.data??r;
const SECTIONS=[
  ['workflow','Progress'],
  ['overview','Case summary'],
  ['clinical','Autopsy & lab'],
  ['forensic','Evidence'],
  ['services','Chapel & burial'],
  ['finance','Billing'],
  ['timeline','Timeline'],
  ['audit','Audit'],
] as const;
const STATE_LABEL:Record<string,string>={DONE:'Done',IN_PROGRESS:'In progress',PENDING:'Waiting',BLOCKED:'Blocked',OPTIONAL:'Optional',SKIPPED:'Skipped'};
const STAGE_CLS: Record<string,string>={
  DONE:'bg-green-100 text-green-800 border-green-200',
  IN_PROGRESS:'bg-sky-100 text-sky-800 border-sky-200',
  PENDING:'bg-slate-100 text-slate-600 border-slate-200',
  BLOCKED:'bg-red-100 text-red-800 border-red-200',
  OPTIONAL:'bg-amber-50 text-amber-800 border-amber-200',
  SKIPPED:'bg-slate-50 text-slate-400 border-slate-200',
};
const NEXT: Record<string,string[]>={
  RECEIVED:['IDENTIFICATION_PENDING','IDENTIFIED','STORAGE_ASSIGNED','AUTOPSY_PENDING','UNCLAIMED'],
  IDENTIFICATION_PENDING:['IDENTIFIED','STORAGE_ASSIGNED','UNCLAIMED'],
  IDENTIFIED:['STORAGE_ASSIGNED','IN_MORTUARY','AUTOPSY_PENDING','RELEASE_PENDING','RELEASED','TRANSFERRED'],
  STORAGE_ASSIGNED:['IN_MORTUARY','AUTOPSY_PENDING','RELEASE_PENDING','RELEASED','TRANSFERRED','UNCLAIMED'],
  IN_MORTUARY:['AUTOPSY_PENDING','AUTOPSY_IN_PROGRESS','RELEASE_PENDING','RELEASED','TRANSFERRED','UNCLAIMED','CEMETERY','CREMATORIUM'],
  AUTOPSY_PENDING:['AUTOPSY_IN_PROGRESS','IN_MORTUARY'],
  AUTOPSY_IN_PROGRESS:['AUTOPSY_COMPLETED'],
  AUTOPSY_COMPLETED:['RELEASE_PENDING','RELEASED','IN_MORTUARY','CEMETERY','CREMATORIUM'],
  RELEASE_PENDING:['RELEASED','TRANSFERRED','CEMETERY','CREMATORIUM','UNCLAIMED'],
  RELEASED:['CEMETERY','CREMATORIUM','FINAL_DISPOSITION_COMPLETED'],
  TRANSFERRED:['CEMETERY','CREMATORIUM','FINAL_DISPOSITION_COMPLETED'],
  UNCLAIMED:['IDENTIFIED','RELEASE_PENDING','CEMETERY','CREMATORIUM'],
  CEMETERY:['FINAL_DISPOSITION_COMPLETED'],
  CREMATORIUM:['FINAL_DISPOSITION_COMPLETED'],
};

const badge=(text:string,cls:string)=>text?<span className={`text-[10px] px-2 py-0.5 rounded-full ${cls}`}>{text}</span>:null;

export default function MortuaryCase360Page(){
  const {id}=useParams();
  const nav=useNavigate();
  const [c,setC]=useState<any>(null);
  const [tab,setTab]=useState<(typeof SECTIONS)[number][0]>('workflow');
  const [dest,setDest]=useState('');
  const [archiveReason,setArchiveReason]=useState('Retention policy');
  const [msg,setMsg]=useState('');
  const [saving,setSaving]=useState('');
  const [next,setNext]=useState('');
  const [reason,setReason]=useState('');
  const [photo,setPhoto]=useState<any>({region:'HEAD',sequenceNo:1});
  const [evd,setEvd]=useState<any>({category:'CLOTHING'});
  const [scan,setScan]=useState('');
  const [spec,setSpec]=useState<any>({specimenType:'BLOOD',purpose:'TOXICOLOGY'});
  const [overrideReason,setOverrideReason]=useState('');

  const load=async()=>{
    try{setC(unwrap(await api.getMortuaryCase360(id as string)));}
    catch(e:any){setMsg(e.response?.data?.message||'Could not load Case 360');}
  };
  useEffect(()=>{load();},[id]);

  const run=async(key:string,fn:()=>Promise<any>,ok='✅ Saved')=>{
    setSaving(key);
    try{await fn();setMsg(ok);await load();}
    catch(e:any){setMsg('❌ '+(e.response?.data?.message||'Action failed'));}
    finally{setSaving('');}
  };

  if(!c) return <Layout><div className="card p-8 text-center text-slate-400">{msg||'Loading Case 360…'}</div></Layout>;
  const b=c.body||{};
  const life=c.lifecycleStatus||b.status;
  const options=NEXT[life]||[];

  return(
    <Layout>
      <div className="space-y-4">
        <div className="flex flex-wrap justify-between gap-2">
          <div>
            <button className="text-xs text-[#02357A]" onClick={()=>nav(`/mortuary/bodies/${id}`)}>← Daily workbench</button>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 mt-1">Full case file</p>
            <h1 className="text-xl font-bold">{c.caseNumber} · {b.full_name||'Unknown'}</h1>
            <p className="text-xs text-slate-500">{prettyMortuary(life)} · {b.body_number} · ID {prettyMortuary(b.identification_status)} · Chamber {b.chamber?.chamber_number||'Unassigned'}</p>
            <p className="text-xs text-slate-500 mt-1">Review the whole case here — autopsy, specimens, evidence, billing and timeline. Identify, store and release on the daily workbench.</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {badge(b.legal_hold?'LEGAL HOLD':'','bg-red-100 text-red-800')}
              {badge(b.medico_legal?'MLC':'','bg-amber-100 text-amber-800')}
              {badge(b.infectious?'INFECTIOUS':'','bg-orange-100 text-orange-800')}
              {badge(c.retentionStatus!=='ACTIVE'?c.retentionStatus:'','bg-slate-100 text-slate-700')}
              {(c.sla||[]).length?badge(`${c.sla.length} SLA`,'bg-pink-100 text-pink-800'):null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary text-sm" onClick={()=>nav(`/mortuary/bodies/${id}`)}>Daily workbench</button>
            <button className="btn-secondary text-sm" onClick={()=>nav(`/mortuary/bodies/${id}/autopsy`)}>Autopsy</button>
            <button className="btn-secondary text-sm" onClick={()=>nav(`/mortuary/services?bodyId=${id}`)}>Chapel / burial</button>
            <button className="btn-secondary text-sm" onClick={()=>nav('/mortuary/iot')}>IoT</button>
            <button disabled={saving==='ch'} className="btn-secondary text-sm" onClick={()=>run('ch',()=>api.raiseMortuaryCharges(id as string),'✅ Charges posted')}>Raise charges</button>
          </div>
        </div>
        {msg&&<div className="text-sm bg-slate-50 border rounded-lg px-3 py-2">{msg}</div>}
        <p className="text-[11px] text-slate-500">{c.lifecycleLine}</p>
        <div className="card p-3 overflow-x-auto">
          <div className="flex gap-1 min-w-[980px]">
            {(c.workflow?.stages||[]).map((s:any)=>(
              <button key={s.key} onClick={()=>setTab('workflow')} className={`flex-1 min-w-[56px] border rounded-lg px-1 py-1 text-center ${STAGE_CLS[s.state]||STAGE_CLS.PENDING} ${c.workflow?.current===s.key?'ring-2 ring-[#02357A]':''}`}>
                <div className="text-[9px] font-semibold">{s.n}</div>
                <div className="text-[10px] leading-tight">{s.label.split('/')[0].split('(')[0].trim()}</div>
                <div className="text-[9px]">{STATE_LABEL[s.state]||s.state}</div>
              </button>
            ))}
          </div>
          {c.workflow?.nextAction&&<p className="text-xs mt-2 text-[#02357A]">Next: {c.workflow.nextAction}</p>}
        </div>

        <div className="card p-3 space-y-2">
          <p className="text-xs text-slate-500">Supervisor tools — most staff should use the daily workbench instead of changing status here.</p>
          <div className="grid md:grid-cols-4 gap-2 items-end">
            <div>
              <label className="label">Change case status</label>
              <Select value={next} onChange={e=>setNext(e.target.value)}>
                <option value="">Choose next status</option>
                {options.map(s=><option key={s}>{s}</option>)}
              </Select>
            </div>
            <Input placeholder="Reason (required for unusual changes)" value={reason} onChange={e=>setReason(e.target.value)}/>
            <button disabled={!next||saving==='tr'} className="btn-primary text-sm" onClick={()=>run('tr',()=>api.transitionMortuaryBody(id as string,{to:next,reason}),'✅ Transition applied')}>Update status</button>
            <div className="flex gap-2">
              <Input placeholder="Scan body tag" value={scan} onChange={e=>setScan(e.target.value)}/>
              <button disabled={!scan||saving==='sc'} className="btn-secondary text-sm" onClick={()=>run('sc',()=>api.recordMortuaryScan({bodyId:id,tag:scan,eventType:'SCAN',location:b.chamber?.chamber_number}),'✅ Scan recorded')}>Scan</button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {SECTIONS.map(([s,label])=>(
            <button key={s} onClick={()=>setTab(s)} className={`text-xs px-3 py-1.5 rounded-full border ${tab===s?'bg-[#02357A] text-white border-[#015CA6]':'border-slate-300 text-slate-600'}`}>{label}</button>
          ))}
        </div>

        {tab==='workflow'&&(
          <div className="space-y-4">
            <div className="card p-4">
              <h3 className="font-semibold mb-2">What still needs to be done</h3>
              <div className="space-y-2">
                {(c.workflow?.stages||[]).map((s:any)=>(
                  <div key={s.key} className={`border rounded-lg px-3 py-2 ${STAGE_CLS[s.state]||''}`}>
                    <div className="flex justify-between gap-2 text-sm">
                      <span className="font-medium">{s.n}. {s.label}</span>
                      <span className="text-xs">{STATE_LABEL[s.state]||s.state}</span>
                    </div>
                    <p className="text-xs mt-0.5">{s.detail}</p>
                    {s.next&&<p className="text-[11px] mt-1">Next: {s.next}</p>}
                  </div>
                ))}
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="card p-4 space-y-2">
                <h3 className="font-semibold">Release authorization checklist</h3>
                {(c.releaseGate?.items||[]).map((i:any)=>(
                  <p key={i.key} className={`text-sm ${i.ok?'text-green-700':'text-red-700'}`}>{i.ok?'✓':'✗'} {i.label}{i.required?' *':''} — {i.detail}</p>
                ))}
                {c.releaseGate?.alreadyClosed || ['RELEASED','DISPOSED','FINAL_DISPOSITION_COMPLETED'].includes(String(life||'').toUpperCase())
                  ? <p className="text-sm text-slate-600">This case is already closed. Billing is on Prepare or this page’s Billing tab.</p>
                  : c.releaseGate?.canRelease
                    ? <p className="text-sm text-green-800">Ready to release from the body workbench.</p>
                    : <p className="text-sm text-amber-800">Blocked: {(c.releaseGate?.blocking||[]).join('; ')||'checklist incomplete'}</p>}
                <button className="btn-secondary text-sm" onClick={()=>nav(`/mortuary/bodies/${id}`)}>Open release / handover</button>
              </div>
              <div className="card p-4 space-y-2">
                <h3 className="font-semibold">Final disposition — choose ONE</h3>
                <p className="text-xs text-slate-500">Burial or cremation or transfer. Completing one closes the others.</p>
                <p className="text-sm">Current: {c.disposition?.kind||c.workflow?.dispositionChoice||'not chosen'}</p>
                <div className="flex flex-wrap gap-2">
                  <button className="btn-secondary text-sm" onClick={()=>nav(`/mortuary/cemetery?bodyId=${id}`)}>Burial board</button>
                  <button className="btn-secondary text-sm" onClick={()=>nav(`/mortuary/crematorium?bodyId=${id}`)}>Cremation board</button>
                  <Input placeholder="Transfer destination" value={dest} onChange={e=>setDest(e.target.value)}/>
                  {b.legal_hold&&<Input placeholder="Override reason (legal hold)" value={overrideReason} onChange={e=>setOverrideReason(e.target.value)}/>}
                  <button disabled={!dest||saving==='xf'||!!c.disposition||(!!b.legal_hold&&!overrideReason.trim())} className="btn-primary text-sm" onClick={()=>run('xf',()=>api.recordMortuaryDisposition({bodyId:id,kind:'TRANSFER',destination:dest,overrideReason}),'✅ Transfer disposition recorded')}>Record transfer</button>
                </div>
                <div className="flex gap-2 items-end">
                  <Input placeholder="Archive reason" value={archiveReason} onChange={e=>setArchiveReason(e.target.value)}/>
                  <button disabled={saving==='ar'||c.retentionStatus==='ARCHIVED'||(c.workflow?.stages||[]).find((s:any)=>s.key==='CLOSURE')?.state!=='DONE'} className="btn-secondary text-sm" onClick={()=>run('ar',()=>api.archiveMortuaryCase(id as string,{reason:archiveReason}),'✅ Case archived')}>Archive</button>
                </div>
                {(c.workflow?.stages||[]).find((s:any)=>s.key==='CLOSURE')?.state!=='DONE'&&<p className="text-[11px] text-amber-800">Archive is available only after final disposition / closure.</p>}
                <p className="text-[11px] text-slate-500">Retention uses facility/country rules (including Congo/DRC defaults). Legal hold blocks archive. No statute is hard-coded.</p>
                <button disabled={saving==='lis'} className="btn-secondary text-sm" onClick={()=>run('lis',()=>api.syncMortuaryLab(id as string),'✅ LIS results synced')}>Return LIS results to case</button>
              </div>
            </div>
          </div>
        )}

        {tab==='overview'&&(
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="card p-4 text-sm space-y-1">
              <h3 className="font-semibold">Identity</h3>
              <p>Case {c.caseNumber}</p>
              <p>Body {b.body_number}</p>
              <p>Patient {b.patient_uhid||b.patient_id||'not linked'}</p>
              <p>Police / MLC {b.police_reference_number||'—'} {b.legal_hold?'· LEGAL HOLD':''}</p>
              <p>Scan token {b.scan_token||'—'} · RFID {b.rfid_tag||'—'}</p>
              <p>Retention {c.retentionStatus}</p>
            </div>
            <div className="card p-4 text-sm space-y-1">
              <h3 className="font-semibold">Location / family</h3>
              <p>Chamber {b.chamber?.chamber_number||'Unassigned'} {b.chamber?.rack_shelf_slot||''}</p>
              <p>Next of kin {(b.nextOfKin||[]).map((x:any)=>x.full_name).join(', ')||'—'}</p>
              <p>Viewing allowed {b.viewing_allowed?'yes':'no'}</p>
              <p>Funeral director {(c.body?.funeralDirector||c.funeralDirector)?.name||'—'}</p>
            </div>
            <div className="card p-4 text-sm">
              <h3 className="font-semibold mb-1">Open SLA</h3>
              {(c.sla||[]).length?(c.sla||[]).map((s:any)=><p key={s.id} className="text-pink-800">{s.policy_code} · {s.severity}</p>):<p className="text-slate-500">None</p>}
              <h3 className="font-semibold mt-3 mb-1">Compliance</h3>
              {(c.compliance?.items||[]).map((i:any)=><p key={i.key} className={i.state==='GREEN'?'text-green-700':i.state==='RED'?'text-red-700':'text-amber-700'}>{i.label}: {i.state}</p>)}
            </div>
          </div>
        )}

        {tab==='clinical'&&(
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="card p-4 text-sm space-y-2">
              <h3 className="font-semibold">Autopsy</h3>
              <p>{c.autopsy?.pm_number||'No PM case'} · {c.autopsy?.status||'—'}</p>
              <p className="text-xs text-slate-500">AI drafts are assistive only. Cause/manner require human sign-off.</p>
              <p className="text-xs">Embalming {(b.embalmings||[]).map((x:any)=>x.status).join(', ')||'none'}</p>
              <button className="btn-secondary text-sm" onClick={()=>nav(`/mortuary/bodies/${id}/autopsy`)}>Open autopsy workbench</button>
              <div>
                <label className="label">Order PM imaging (existing PACS)</label>
                <div className="flex gap-2">
                  {['XR','CT','DX'].map(m=>(
                    <button key={m} disabled={!b.patient_id||saving===`im${m}`} className="btn-secondary text-xs" onClick={()=>run(`im${m}`,()=>api.orderMortuaryImaging(id as string,{modality:m}))}>{m}</button>
                  ))}
                </div>
                {!b.patient_id&&<p className="text-[11px] text-amber-800">Link a patient on the body record before ordering existing PACS imaging.</p>}
              </div>
            </div>
            <div className="card p-4 space-y-2">
              <h3 className="font-semibold">Specimens → LIS</h3>
              <div className="grid grid-cols-2 gap-2">
                <Select value={spec.specimenType} onChange={e=>setSpec({...spec,specimenType:e.target.value})}>
                  {['BLOOD','URINE','VITREOUS','LIVER','KIDNEY','BRAIN','SWAB','DNA'].map(s=><option key={s}>{s}</option>)}
                </Select>
                <Select value={spec.purpose} onChange={e=>setSpec({...spec,purpose:e.target.value})}>
                  {['TOXICOLOGY','HISTOPATHOLOGY','MICROBIOLOGY','DNA','OTHER'].map(s=><option key={s}>{s}</option>)}
                </Select>
              </div>
              <div className="flex gap-2">
                <button disabled={saving==='sp'} className="btn-primary text-sm" onClick={()=>run('sp',async()=>{
                  const r=await api.addMortuarySpecimen(id as string,spec);
                  const d=(r as any)?.data?.data||(r as any)?.data||r;
                  if(d&&!d.lab_request_id) setMsg('Specimen collected. LIS order is created only when the body is linked to a patient.');
                })}>Add specimen</button>
                <button disabled={saving==='lis2'} className="btn-secondary text-sm" onClick={()=>run('lis2',()=>api.syncMortuaryLab(id as string),'✅ LIS results synced')}>Sync LIS results</button>
              </div>
              <DataTable data={c.specimens||[]} emptyTitle="No specimens" columns={[{key:'specimen_number',label:'ID'},{key:'specimen_type',label:'Type'},{key:'purpose',label:'Purpose'},{key:'status',label:'Status'}]}/>
            </div>
            <div className="card p-4">
              <h3 className="font-semibold mb-2">Radiology (existing PACS)</h3>
              <DataTable data={c.imaging||[]} emptyTitle="No imaging orders" columns={[{key:'order_number',label:'Order'},{key:'modality',label:'Modality'},{key:'status',label:'Status'}]}/>
            </div>
            <div className="card p-4">
              <h3 className="font-semibold mb-2">Belongings / documents</h3>
              <DataTable data={b.belongings||[]} emptyTitle="No belongings" columns={[{key:'item_description',label:'Item'},{key:'quantity',label:'Qty'}]}/>
              <DataTable data={b.documents||[]} emptyTitle="No documents" columns={[{key:'document_type',label:'Type'},{key:'file_name',label:'File'}]}/>
            </div>
          </div>
        )}

        {tab==='forensic'&&(
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="card p-4 space-y-2">
              <h3 className="font-semibold">Evidence</h3>
              <DataTable data={c.evidence||[]} emptyTitle="No evidence" columns={[{key:'evidence_number',label:'ID'},{key:'category',label:'Type'},{key:'description',label:'Description'}]}/>
              <div className="grid grid-cols-2 gap-2">
                <Select value={evd.category} onChange={e=>setEvd({...evd,category:e.target.value})}>
                  {['CLOTHING','WEAPON','BIOLOGICAL','DOCUMENT','PHOTOGRAPH','DIGITAL','BELONGING','OTHER'].map(s=><option key={s}>{s}</option>)}
                </Select>
                <Input placeholder="Description" value={evd.description||''} onChange={e=>setEvd({...evd,description:e.target.value})}/>
                <Input placeholder="Seal number" value={evd.sealNumber||''} onChange={e=>setEvd({...evd,sealNumber:e.target.value})}/>
                <Input placeholder="Storage location" value={evd.storageLocation||''} onChange={e=>setEvd({...evd,storageLocation:e.target.value})}/>
              </div>
              <button disabled={saving==='ev'} className="btn-primary text-sm" onClick={()=>run('ev',()=>api.addMortuaryEvidence(id as string,evd))}>Record evidence</button>
            </div>
            <div className="card p-4 space-y-2">
              <h3 className="font-semibold">Photography</h3>
              <DataTable data={c.photos||[]} emptyTitle="No photos" columns={[{key:'sequence_no',label:'#'},{key:'region',label:'Region'},{key:'description',label:'Description'}]}/>
              <div className="grid grid-cols-2 gap-2">
                <Select value={photo.region} onChange={e=>setPhoto({...photo,region:e.target.value})}>
                  {['HEAD','TORSO','LIMBS','EXTERNAL','INTERNAL','SCENE','OTHER'].map(s=><option key={s}>{s}</option>)}
                </Select>
                <Input type="number" value={photo.sequenceNo||1} onChange={e=>setPhoto({...photo,sequenceNo:e.target.value})}/>
                <Input placeholder="Photographer" value={photo.photographer||''} onChange={e=>setPhoto({...photo,photographer:e.target.value})}/>
                <Input placeholder="Description / file ref" value={photo.description||''} onChange={e=>setPhoto({...photo,description:e.target.value,fileRef:e.target.value})}/>
              </div>
              <button disabled={saving==='ph'} className="btn-primary text-sm" onClick={()=>run('ph',()=>api.addMortuaryPhoto(id as string,photo))}>Record photo metadata</button>
              <p className="text-[11px] text-slate-500">Files use the existing mortuary upload category. Hash is stored when a file ref is provided.</p>
            </div>
          </div>
        )}

        {tab==='services'&&(
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="card p-4">
              <h3 className="font-semibold mb-2">Chapel</h3>
              <DataTable data={c.chapel||[]} emptyTitle="None" columns={[{key:'booking_number',label:'Booking'},{key:'service_type',label:'Type'},{key:'status',label:'Status'}]}/>
              <button className="btn-secondary text-sm mt-2" onClick={()=>nav(`/mortuary/chapel?bodyId=${id}`)}>Book chapel</button>
            </div>
            <div className="card p-4 space-y-2">
              <h3 className="font-semibold">Transport / burial / cremation</h3>
              <DataTable data={c.trips||[]} emptyTitle="No trips" columns={[{key:'trip_number',label:'Trip'},{key:'purpose',label:'Purpose'},{key:'status',label:'Status'}]}/>
              <DataTable data={c.burials||[]} emptyTitle="No burials" columns={[{key:'burial_number',label:'Burial'},{key:'plot_code',label:'Plot'},{key:'status',label:'Status'}]}/>
              <DataTable data={c.cremations||[]} emptyTitle="No cremations" columns={[{key:'cremation_number',label:'Cremation'},{key:'status',label:'Status'}]}/>
              <p className="text-xs">Disposition: {c.disposition?.kind||'pending'} {c.disposition?.destination||''}</p>
              <div className="flex flex-wrap gap-2">
                <button className="btn-secondary text-xs" onClick={()=>nav(`/mortuary/transport?bodyId=${id}`)}>Schedule trip</button>
                <button className="btn-secondary text-xs" onClick={()=>nav(`/mortuary/cemetery?bodyId=${id}`)}>Schedule burial</button>
                <button className="btn-secondary text-xs" onClick={()=>nav(`/mortuary/crematorium?bodyId=${id}`)}>Book cremation</button>
              </div>
            </div>
          </div>
        )}

        {tab==='finance'&&(
          <div className="card p-4">
            <h3 className="font-semibold mb-2">Charges (existing billing engine)</h3>
            <DataTable data={c.billing?.events||[]} emptyTitle="No charge events" columns={[{key:'service_code',label:'Code'},{key:'description',label:'Description'},{key:'amount',label:'Amount'},{key:'idempotency_key',label:'Idempotency'}]}/>
            {c.billing?.invoice
              ? <p className="text-sm mt-2">Invoice {c.billing.invoice.bill_number||c.billing.invoice.id} · {c.billing.invoice.status} · balance {c.billing.invoice.balance_amount}</p>
              : (c.billing?.events||[]).length
                ? <p className="text-sm text-amber-800 mt-2">Fees are recorded, but no HMIS bill exists yet. Click Raise charges to post them.</p>
                : <p className="text-sm text-slate-500 mt-2">No charges yet. Click Raise charges to post storage, release and preparation fees.</p>}
          </div>
        )}

        {tab==='timeline'&&(
          <div className="card p-4 text-sm space-y-1 max-h-[70vh] overflow-auto">
            {(c.timeline||[]).map((t:any,i:number)=>(
              <p key={i}>{fmtDateTime(t.occurred_at)} · {t.title||t.event_type} {t.detail?`· ${t.detail}`:''} <span className="text-slate-400">{t.source}</span></p>
            ))}
            {!(c.timeline||[]).length&&<p className="text-slate-500">No timeline events yet</p>}
          </div>
        )}

        {tab==='audit'&&(
          <div className="card p-4 text-sm space-y-1 max-h-[70vh] overflow-auto">
            {(b.audit||[]).map((a:any)=>(
              <p key={a.id}>{fmtDateTime(a.created_at)} · {a.action} · {a.entity} · {a.first_name||a.email||'system'} {a.reason?`· ${a.reason}`:''}</p>
            ))}
            {(c.events||[]).map((e:any)=>(
              <p key={e.id} className="text-slate-500">{fmtDateTime(e.created_at)} · EVENT {e.event_type} · {e.event_key}</p>
            ))}
            {!(b.audit||[]).length&&!(c.events||[]).length&&<p className="text-slate-500">No audit rows yet</p>}
          </div>
        )}
      </div>
    </Layout>
  );
}
